import { TextFieldModule } from '@angular/cdk/text-field';
import { ChangeDetectionStrategy, Component, inject, input, output, signal } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslocoModule } from '@jsverse/transloco';
import { FroalaViewModule } from 'angular-froala-wysiwyg';
import { produce } from 'immer';
import { LinksService } from 'shared/domain';
import { ConfirmationDialogComponent, ConfirmationDialogArgs, DialogService, IconDirective, ButtonDirective } from 'shared/ui-common';

import { LinkElementType, GuidelineLinkDto } from '@treetop/shared/model';

import { AnchorLinkEditComponent } from './anchor-link-edit/anchor-link-edit.component';
import { AnchorLinkItemComponent } from './anchor-link-item/anchor-link-item.component';

@Component({
  standalone: true,
  imports: [
    MatButtonModule,
    TranslocoModule,
    TextFieldModule,
    FormsModule,
    MatButtonModule,
    ReactiveFormsModule,
    FroalaViewModule,

    MatIconModule,
    IconDirective,
    AnchorLinkItemComponent,
    AnchorLinkEditComponent,
    ButtonDirective,
  ],
  selector: 'guideline-sidebar-anchor-link',
  templateUrl: './anchor-link.component.html',
  styleUrls: ['./anchor-link.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AnchorLinkComponent {
  private dialogService = inject(DialogService);
  public linksService = inject(LinksService);

  public links = input<GuidelineLinkDto[]>([]);
  public linksChanged = output<GuidelineLinkDto[]>();

  protected currentLink = signal<GuidelineLinkDto | null>(undefined);

  protected saveLink(link: GuidelineLinkDto) {
    const convertedLink = this.convertLink(link);

    if (!this.links()) {
      this.linksChanged.emit([convertedLink]);
    } else {
      const updatedLinks = produce(this.links(), (draft) => {
        const index = draft.findIndex((actLink) => actLink.id === convertedLink.id);

        if (index !== -1) {
          Object.assign(draft[index], convertedLink);
        } else {
          draft.push(convertedLink);
        }
      });
      this.linksChanged.emit(updatedLinks);
    }

    this.cancelEdit();
  }

  protected removeLink(id: string) {
    const args: ConfirmationDialogArgs = {
      message: 'DeleteLinkMsg',
      cancelButtonCaption: 'Cancel',
      confirmButtonCaption: 'Delete',
    };

    const deleteDialog = this.dialogService.open(ConfirmationDialogComponent, {
      icon: 'circle-x',
      title: 'DeleteLink',
      data: args,
      disableClose: true,
    });

    deleteDialog.afterClosed().subscribe((result) => {
      if (result) {
        this.linksChanged.emit(this.links().filter((link) => link.id !== id));
      }
    });
  }

  protected cancelEdit() {
    this.currentLink.set(null);
  }

  protected handleAddLinkButtonClicked() {
    this.currentLink.set({
      id: undefined,
      title: '',
      description: '',
    });
  }

  private convertLink(link: GuidelineLinkDto): GuidelineLinkDto {
    const linkCopy = { ...link };

    if (!linkCopy.id) {
      linkCopy.id = crypto.randomUUID();
    }

    return linkCopy;
  }

  protected LinkElementType = LinkElementType;
}
